import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import bannerImg from "../assets/images/shutterstock_2811192509.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowRightLong,
  faAward,
  faBath,
  faBed,
  faEnvelope,
  faLocationDot,
  faMagnifyingGlass,
  faPhone,
  faRulerCombined,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import avatar1 from "../assets/images/avatar1.png";
import avatar2 from "../assets/images/avatar3.png";
import placeholder from "../assets/images/house-placeholder.webp";
import { Link } from "react-router-dom";
import imgs from "./temp.json";
import ReactImageGallery from "react-image-gallery";
import { motion } from "framer-motion";
import calc from "../assets/images/shutterstock_2489188913-min.jpg";
import report from "../assets/images/shutterstock_699442570-min.jpg";
import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import formattingFunctions from "../functions/formatting";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";
import globalState from "../utils/globalState";
import { useUserParams } from "../hooks/useUserParams";
import { buttonAnimation, fadeUp, stagger } from "../functions/animations";
import Contact from "./contact.component";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";

function toDisplayText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value.map(toDisplayText).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    if (value.highlight != null) return toDisplayText(value.highlight);
    if (typeof value.text === "string") return value.text;
    if (typeof value.value === "string") return value.value;
    if (Array.isArray(value.content)) {
      return value.content.map(toDisplayText).filter(Boolean).join(" ");
    }
    for (const nested of Object.values(value)) {
      const text = toDisplayText(nested);
      if (text) return text;
    }
  }
  return "";
}

function toImageSrc(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.url || value.src || value.image || null;
  }
  return null;
}

function normalizeTestimonialsList(rawTestimonials) {
  let list;
  if (Array.isArray(rawTestimonials)) {
    list = rawTestimonials;
  } else if (typeof rawTestimonials === "string") {
    try {
      const parsed = JSON.parse(rawTestimonials);
      return normalizeTestimonialsList(parsed);
    } catch {
      return [];
    }
  } else if (rawTestimonials && typeof rawTestimonials === "object") {
    if (Array.isArray(rawTestimonials.items)) {
      list = rawTestimonials.items;
    } else if (Array.isArray(rawTestimonials.data)) {
      list = rawTestimonials.data;
    } else {
      list = Object.values(rawTestimonials).filter(
        (item) => item != null && typeof item !== "string"
      );
    }
  } else {
    return [];
  }

  return list
    .map((item, index) => {
      if (item == null) return null;
      if (typeof item === "string") {
        return {
          name: `Testimonial ${index + 1}`,
          quote: item,
          image: null,
          location: "",
        };
      }
      if (typeof item !== "object") return null;

      const quote = toDisplayText(
        item.quote ?? item.description ?? item.highlight ?? item.text
      );
      const name =
        toDisplayText(item.name) || `Testimonial ${index + 1}`;
      const location = toDisplayText(
        item.location ?? item.role ?? item.title
      );

      return {
        name,
        quote,
        image: toImageSrc(item.image ?? item.img),
        location,
      };
    })
    .filter((item) => item && (item.quote || item.name));
}

export default function Homepage() {
  const proxyUrl = "https://cors-anywhere.herokuapp.com/";
  const loaded = useRef(false);
  const {
    getCurrentAgentName,
    getCurrentAbout,
    getCurrentHeadshot,
    getCurrentTestimonials,
  } = useUserParams();

  const rawTestimonials = getCurrentTestimonials();
  const testimonials = useMemo(
    () => normalizeTestimonialsList(rawTestimonials),
    [rawTestimonials]
  );

  // State
  // const [listings, setListings] = useState([]); //for production
  const [listings, setListings] = useState(data.value); //for local testing

  const [media, setMedia] = useState(imgs.thumbs.value);
  const [formatted, setFormatted] = useState({});
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");

  function getData() {
    // Skip API calls during static generation
    if (isReactSnap()) {
      return Promise.resolve({ value: [] });
    }

    var body = {
      url: `https://query.ampre.ca/odata/Property?$filter=(ListOfficeKey eq '${constants.OFFICE_ID}' and StandardStatus eq 'Active')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=12`,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    return new Promise(async (resolve, reject) => {
      try {
        let request = `odata/Property(Q)$filter=(ListOfficeKey(S)eq(S)\'${constants.OFFICE_ID}\'(S)and(S)StandardStatus(S)eq(S)\'Active\')(A)$orderby=OriginalEntryTimestamp(S)desc,ListingKey(A)$top=12`;


        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${request}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function getMedia(listings) {
    // Skip API calls during static generation
    if (isReactSnap() || !listings || listings.length === 0) {
      return Promise.resolve({ value: [] });
    }

    // this is for development
    //var temp = `https://query.ampre.ca/odata/Media?$filter=(`

    // this is for production
    // var temp = `odata/Media?$filter=(`

    if (constants.ENVIRONMENT === "development") {
      var temp = `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "', ";
      }
      temp = temp.slice(0, -2);
      temp =
        temp +
        ") and ImageSizeDescription eq 'Medium' and PreferredPhotoYN eq true";
    } else {
      var temp = `odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "',";
      }
      temp = temp.slice(0, -1);
      temp =
        temp +
        ")(S)and(S)ImageSizeDescription(S)eq(S)'Medium'(S)and(S)PreferredPhotoYN(S)eq(S)true";
    }
    var body = {
      url: temp,
      needAuth: true,
      bearerToken: constants.TOKEN,
      // Accept: 'application/json',
      // Authorization: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ2ZW5kb3IvdHJyZWIvNjUyMCIsImF1ZCI6IkFtcFVzZXJzUHJkIiwicm9sZXMiOlsiQW1wVmVuZG9yIl0sImlzcyI6InByb2QuYW1wcmUuY2EiLCJleHAiOjI1MzQwMjMwMDc5OSwiaWF0IjoxNzMwMjI1OTA1LCJzdWJqZWN0VHlwZSI6InZlbmRvciIsInN1YmplY3RLZXkiOiI2NTIwIiwianRpIjoiMGYyMjk3OTY1Y2NjN2U4MSIsImN1c3RvbWVyTmFtZSI6InRycmViIn0.BMvVrtwah8swIzW38UMs6yKF-HM4QO0bZu81blYx6J4'
    };

    return new Promise(async (resolve, reject) => {
      try {
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${temp}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    var temp = {};

    for (let img of media) {
      if (temp[img.ResourceRecordKey]) {
        temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img];
      } else {
        temp[img.ResourceRecordKey] = [img];
      }
    }
    return temp;
  }

  const getURLParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      userId: urlParams.get("userId"),
    };
  };

  const fetchTemplateSettings = async (userId) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await fetch(
        `https://agent.proagentsolutions.com/MongoDB/GetTemplateSettingsByUserId?userId=${userId}&apiKey=${apiKey}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const templateSettings = await response.json();
      console.log("Template settings fetched:", templateSettings);

      if (templateSettings) {
        const settingsData = templateSettings.data || templateSettings;

        const mappedUserData = {
          userId: userId,
          agentName: settingsData.agentName || settingsData.AGENT_NAME,
          agentTitle: settingsData.agentTitle || settingsData.AGENT_TITLE,
          agentNumber: settingsData.agentNumber || settingsData.AGENT_NUMBER,
          agentEmail: settingsData.agentEmail || settingsData.AGENT_EMAIL,
          brokerage: settingsData.brokerage || settingsData.BROKERAGE,
          ...settingsData,
        };

        globalState.setUserData(mappedUserData);

        console.log("Template settings data:", settingsData);
        console.log("Template settings mapped to user data:", mappedUserData);
      }

      return templateSettings;
    } catch (error) {
      console.error("Error fetching template settings:", error);
      return null;
    }
  };

  useEffect(() => {
    const urlParams = getURLParameters();
    if (urlParams.userId) {
      globalState.setUserId(urlParams.userId);
      fetchTemplateSettings(urlParams.userId).then(() => {
        console.log(
          "Global State Updated with Template Settings:",
          globalState.getDebugInfo()
        );
      });
      console.log("Global State Updated:", globalState.getDebugInfo());
    }
  }, []);

  // Data fetching effect with React Snap compatibility
  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    const isReactSnapEnv =
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("ReactSnap");
    const isBrowser = typeof window !== "undefined";

    if (isBrowser && !isReactSnapEnv && !loaded.current) {
      getData()
        .then((data) => {
          setListings(data.value);
          getMedia(data.value)
            .then((d) => {
              setMedia(d.value);
              setFormatted(formatMedia(d.value));
            })
            .catch((err) => {
              console.log(err);
            });
        })
        .catch((err) => {
          console.log(err);
        });
      loaded.current = true;
    }
  }, []);

  function slugify(str) {
    str = str.replace(/^\s+|\s+$/g, "");
    str = str.toLowerCase();
    str = str
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    return str;
  }

  const seoConfig = pageSEO.home();

  return (
    <>
      <SEO {...seoConfig} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            src={bannerImg}
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{
              duration: 8,
              ease: "easeOut",
            }}
          />
        </div>
        <div class="absolute inset-0 bg-black/30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/10 to-transparent" />

        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="relative z-10 text-center max-w-4xl mx-auto px-6">
          <div className="mx-auto max-w-7xl px-0 md:px-8">
            <h1 className="text-lg font-light leading-[0.95] mb-8 text-white flex flex-col gap-5">
              <motion.span
                variants={fadeUp}
                className="uppercase tracking-wide md:tracking-widest font-light"
              >Experience you can Trust
              </motion.span>

              <motion.span
                variants={fadeUp}
                className="text-5xl md:text-7xl lg:text-8xl text-summit-highlight font-heading font-light"
              >
                {getCurrentAgentName()}
              </motion.span>
            </h1>

            <motion.p
              variants={fadeUp}
              className="my-8 max-w-xl mx-auto text-xl text-summit-muted"
            >
              Your trusted real estate partner for life. Find your dream home or sell your property with our expert guidance.
            </motion.p>

            <motion.div
              variants={buttonAnimation}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/explore" className="btn-luxury">
                Browse Listings
              </Link>

              <Link to="#apply" className="btn-luxury-outline">
                Contact Us
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="flex flex-col lg:flex-row min-h-[650px]">
        <motion.div
          initial={{ opacity: 0, x: -200 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="w-full lg:w-[70%] px-5 py-14 lg:px-40 flex flex-col justify-center bg-summit-muted/10 gap-10">
          <h3
            className="uppercase text-summit-highlight text-lg tracking-widest">About</h3>
          <h2
            className="uppercase tracking-widest text-3xl font-medium">{getCurrentAgentName()}</h2>
          <p
            className="text-summit-tertiary whitespace-pre-line">{getCurrentAbout()}</p>
          <Link
            to="/about"
            className="btn-luxury-outline w-fit">View More</Link>
        </motion.div>

        <div className="w-full lg:w-[30%]">
          <img
            src={getCurrentHeadshot()}
            alt={getCurrentAgentName()}
            className="w-full h-full object-cover block"
          />
        </div>
      </section>

      {/* Feature Listings Section */}
      <motion.section
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="mx-auto max-w-[1600px] px-6 py-24">
        {/* Heading */}
        <div
          className="mb-14 text-center">
          <motion.p
            variants={fadeUp}
            className="uppercase tracking-[0.4em] text-summit-highlight text-sm">
            Premium Collection
          </motion.p>

          <motion.h2
            variants={fadeUp}
            className="mt-3 text-4xl font-light uppercase tracking-wider text-summit-primary">
            Featured Listings
          </motion.h2>

          <div className="mx-auto mt-5 h-[2px] w-24 bg-summit-highlight" />
        </div>

        {/* Cards */}
        <motion.div
          variants={fadeUp}
          className="grid gap-10 md:grid-cols-2 xl:grid-cols-3">
          {listings?.slice(0, 6).map((prop) => (
            <Link
              key={prop.ListingKey}
              to={`/listing/${prop.ListingKey}`}
              className="group block"
            >
              <div className="relative h-[400px] overflow-hidden rounded-lg shadow-xl">
                {/* Image */}
                <img
                  // src={
                  //   formatted[prop.ListingKey]?.find((img) => img.Order === 0)?.MediaURL ??
                  //   formatted[prop.ListingKey]?.[0]?.MediaURL ??
                  //   placeholder
                  // }
                  src={prop.MainImgUrl} //dummy image
                  alt={`${prop.StreetNumber} ${prop.StreetName}`}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

                {/* Price */}
                <div className="absolute top-6 flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 w-full px-5">
                  <div className="rounded-full bg-summit-highlight px-5 py-2 text-lg font-semibold text-white shadow-lg backdrop-blur-sm w-fit">
                    ${prop.ListPrice?.toLocaleString()}
                  </div>

                  {/* Property Type */}
                  <div className="rounded-full bg-white/20 px-4 py-2 text-sm uppercase tracking-wider text-white backdrop-blur-md w-fit">
                    {prop.PropertyType}
                  </div>
                </div>

                {/* Bottom Content */}
                <div className="absolute bottom-0 left-0 right-0 p-7 text-white">
                  <p className="mb-2 flex items-center text-sm uppercase tracking-[0.3em] text-summit-highlight">
                    <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
                    {prop.CityRegion ?? prop.CrossStreet}
                  </p>

                  <h3 className="text-2xl font-light leading-tight">
                    {prop.StreetNumber} {prop.StreetName} {prop.StreetSuffix}
                  </h3>

                  {/* Divider */}
                  <div className="my-5 h-px bg-white/20" />

                  {/* Features */}
                  <div className="flex flex-col lg:flex-row gap-2 lg:items-center justify-between">
                    <div className="flex gap-8">
                      {prop.PropertyType !== "Commercial" && (
                        <>
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon
                              icon={faBed}
                              className="text-summit-highlight"
                            />
                            <span>{prop.BedroomsTotal || prop.BedroomsTotalInteger}</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon
                              icon={faBath}
                              className="text-summit-highlight"
                            />
                            <span>{prop.BathroomsTotalInteger}</span>
                          </div>
                        </>
                      )}

                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={faRulerCombined}
                          className="text-summit-highlight"
                        />
                        <span>{prop.LivingAreaRange ?? "--"} sq.ft.</span>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="w-fit rounded-full border border-white/40 px-5 py-2 text-sm uppercase tracking-widest transition-all duration-300 group-hover:bg-white group-hover:text-black">
                      View
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </motion.div>

        <motion.div
          variants={buttonAnimation}
          className="mt-14 flex justify-center">
          <Link
            to="/listings"
            className="btn-luxury w-fit">View All Listings <FontAwesomeIcon icon={faArrowRight} className="ml-3" /></Link>
        </motion.div>
      </motion.section>

      {/* Testimonials Section */}
      <section className="bg-summit-primary text-white">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1600px] px-6 py-24">
          {/* Heading */}
          <div
            className="mb-14 text-center">
            <motion.p
              variants={fadeUp}
              className="uppercase tracking-[0.4em] text-summit-highlight text-sm">
              what our clients say
            </motion.p>

            <motion.h2
              variants={fadeUp}
              className="mt-3 text-4xl font-light uppercase tracking-wider text-summit-background">
              Testimonials
            </motion.h2>

            <div className="mx-auto mt-5 h-[2px] w-24 bg-summit-highlight" />
          </div>

          {/* Cards */}
          <div className="mx-auto max-w-6xl">
            <Swiper
              modules={[Autoplay]}
              loop
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              spaceBetween={30}
              breakpoints={{
                0: {
                  slidesPerView: 1,
                },
                768: {
                  slidesPerView: 2,
                },
                1280: {
                  slidesPerView: 3,
                },
              }}
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <motion.div
                    variants={fadeUp}
                    className="flex h-[200px] flex-col rounded-xl bg-neutral-700/50 p-6 shadow-lg"
                  >
                    <p className="line-clamp-3 overflow-hidden text-sm leading-6 text-summit-muted tracking-wider">
                      "{testimonial.quote}"
                    </p>

                    <div className="mt-auto flex items-center gap-4">
                      {testimonial.image ? (
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-summit-highlight font-semibold text-white">
                          {testimonial.name
                            .split(" ")
                            .map((word) => word[0])
                            .slice(0, 2)
                            .join("")
                            .toUpperCase()}
                        </div>
                      )}

                      <div>
                        <h3 className="font-semibold text-summit-muted tracking-wider">
                          {testimonial.name}
                        </h3>

                        <p className="text-sm uppercase tracking-widest text-summit-highlight">
                          {testimonial.location}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </motion.div>
      </section>

      {/* Useful tools section  */}
      <section className="bg-summit-muted/10 border-b border-muted">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1600px] px-6 py-24">
          {/* Heading */}
          <div
            className="mb-14 text-center">
            <motion.p
              variants={fadeUp}
              className="uppercase tracking-[0.4em] text-summit-highlight text-sm">
              Get help with
            </motion.p>

            <motion.h2
              variants={fadeUp}
              className="mt-3 text-4xl font-light uppercase tracking-wider text-summit-primary">
              Useful Tools
            </motion.h2>

            <div className="mx-auto mt-5 h-[2px] w-24 bg-summit-highlight" />
          </div>

          {/* Cards */}
          <motion.div
            variants={fadeUp}
            className="grid gap-5 lg:gap-12 md:grid-cols-3"
          >
            {[
              {
                title: "Mortgage Calculator",
                description:
                  "Find a price range that works with your budget by using our Mortgage Calculator.",
                image: calc,
                link: "/calculator",
              },
              {
                title: "Real Estate Reports",
                description:
                  "Gain valuable insights into the market with our free Real Estate Reports.",
                image: report,
                link: "/real-estate-reports",
              },
              {
                title: "Home Evaluation",
                description:
                  "Discover your home's true market value with our professional Home Evaluation.",
                image: house,
                link: "/evaluation",
              },
            ].map((card, index) => (
              <Link
                key={index}
                to={card.link}
                className="group relative"
              >
                <div className="relative h-[400px] md:h-[500px] overflow-hidden rounded-xl shadow-xl transition-all duration-700 group-hover:-translate-y-3 group-hover:shadow-2xl">

                  {/* Image */}
                  <img
                    src={card.image}
                    alt={card.title}
                    className="h-full w-full object-cover transition duration-1000 group-hover:scale-110"
                  />

                  {/* Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                  {/* Glass Content */}
                  <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-white/20 bg-white/10 p-5 backdrop-blur-xl transition-all duration-500 group-hover:bg-white/20">
                    <div className="flex min-h-[200px] md:min-h-[250px] flex-col">

                      <h3 className="text-lg font-light uppercase tracking-wide text-white lg:text-2xl">
                        {card.title}
                      </h3>

                      <div className="my-4 h-[2px] w-12 bg-summit-highlight transition-all duration-500 group-hover:w-24" />

                      <p className="text-sm leading-6 text-white/80 tracking-wider">
                        {card.description}
                      </p>

                      <div className="mt-auto pt-6 flex items-center justify-between">
                        <span className="text-xs uppercase tracking-[0.3em] text-summit-highlight">
                          Learn More
                        </span>

                        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 transition-all duration-500 group-hover:bg-summit-highlight group-hover:border-summit-highlight">
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className="text-white transition-transform duration-300 group-hover:translate-x-1"
                          />
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Contact />
    </>
  );
}
